import { useMemo } from 'react';

import dayjs from 'dayjs';
import { groupBy, orderBy } from 'lodash';
import pluralize from 'pluralize';

import { FeedEvent } from '@queries/EventQueries';

import { EntertainerScheduleDateDetail } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';

enum EventSectionTitle {
  PastShows = 'Past Shows',
  ThisWeek = 'This Week',
  Recent = 'Recent',
  ComingUp = 'Coming Up',
}

const getEventSectionTitle = (
  event: { startDate: string },
  isCurrentEntertainer: boolean,
  now: dayjs.Dayjs = dayjs(),
): EventSectionTitle => {
  const isEventBeforeToday = dayjs(event.startDate).isBefore(now, 'day');

  if (isEventBeforeToday) {
    if (isCurrentEntertainer) {
      // within the last 3 days
      const isWithinReflectionPeriod = dayjs(event.startDate).isAfter(
        dayjs().subtract(3, 'days'),
        'day',
      );

      if (isWithinReflectionPeriod) {
        return EventSectionTitle.Recent;
      }
    }
    return EventSectionTitle.PastShows;
  }

  // Current Week is Monday - Sunday
  const isThisWeek = now.isSame(event.startDate, 'isoWeek');

  if (isThisWeek) {
    return EventSectionTitle.ThisWeek;
  }

  return EventSectionTitle.ComingUp;
};

const SORT_EVENTS_BY_SECTION_TITLE: Record<
  EventSectionTitle,
  <T extends { startDate: string }>(events: T[]) => T[]
> = {
  [EventSectionTitle.ThisWeek]: (events) => orderBy(events, 'startDate', 'asc'),
  [EventSectionTitle.ComingUp]: (events) => orderBy(events, 'startDate', 'asc'),
  [EventSectionTitle.PastShows]: (events) => orderBy(events, 'startDate', 'desc'),
  [EventSectionTitle.Recent]: (events) => orderBy(events, 'startDate', 'desc'),
};

export const useShowsGroupedBySectionTitle = ({
  events,
  scheduleDateDetails,
  isCurrentEntertainer,
}: {
  events: FeedEvent[];
  scheduleDateDetails: Omit<EntertainerScheduleDateDetail, 'entertainer'>[];
  isCurrentEntertainer: boolean;
}) => {
  const mergedEvents = useMemo(() => {
    const now = dayjs();
    return [
      ...events.map((event) => ({
        ...event,
        sectionTitle: getEventSectionTitle(event, isCurrentEntertainer, now),
      })),
      ...scheduleDateDetails.map((scheduleDateDetail) => ({
        ...scheduleDateDetail,
        startDate: scheduleDateDetail.date,
        sectionTitle: getEventSectionTitle(
          { startDate: scheduleDateDetail.date },
          isCurrentEntertainer,
          now,
        ),
      })),
    ];
  }, [events, scheduleDateDetails]);

  const showsGroupedBySectionTitle = useMemo<
    Record<string, (FeedEvent | Omit<EntertainerScheduleDateDetail, 'entertainer'>)[]>
  >(() => {
    if (!mergedEvents?.length) {
      return {};
    }

    const groupedEvents = groupBy(mergedEvents, 'sectionTitle');

    return Object.fromEntries(
      [EventSectionTitle.ThisWeek, EventSectionTitle.ComingUp, EventSectionTitle.PastShows]
        .map((sectionTitle) => ({
          sectionTitle: `${sectionTitle} — ${groupedEvents[sectionTitle]?.length ?? 0} ${pluralize('Show', groupedEvents[sectionTitle]?.length ?? 0)}`,
          events: SORT_EVENTS_BY_SECTION_TITLE[sectionTitle](groupedEvents[sectionTitle] ?? []),
        }))
        .filter(({ events }) => events.length)
        .map(({ sectionTitle, events }) => [sectionTitle, events]),
    );
  }, [mergedEvents, isCurrentEntertainer]);

  return {
    hasShows: Object.keys(showsGroupedBySectionTitle).length > 0,
    hasUpcomingShows: [EventSectionTitle.ThisWeek, EventSectionTitle.ComingUp].some(
      (sectionTitle) => mergedEvents.find((event) => event.sectionTitle === sectionTitle),
    ),
    showsGroupedBySectionTitle,
  };
};
